// src/components/assign/AssignStaffPopover.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "../../api/axios";
import "./AssignStaffPopover.css";

export default function AssignStaffPopover({
  open,
  anchorEl,
  title,
  subtitle,

  headers,
  staffEndpoint = "/backoffice/gos/staff",
  staffParams = {},

  value = [],
  onChange,
  onClose,
  onSave,
  saving = false,

  loadingText = "Loading...",
  emptyText = "Empty",
  cancelText = "Cancel",
  saveText = "Save",

  searchPlaceholder = "Search...",
}) {
  const popoverRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);

  const [q, setQ] = useState("");

  const selectedIds = useMemo(() => (Array.isArray(value) ? value : []), [value]);

  const calcPos = useCallback(() => {
    if (!anchorEl) return;
    const r = anchorEl.getBoundingClientRect();

    const popW = 340;
    const left = Math.min(Math.max(8, r.right - popW), window.innerWidth - popW - 8);

    const preferTop = r.bottom + 10 + 360 > window.innerHeight;
    const top = preferTop ? Math.max(8, r.top - 10) : Math.min(r.bottom + 8, window.innerHeight - 12);

    setPos({ top, left });
  }, [anchorEl]);

  // reset search on close
  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  // load staff on open
  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    setStaffLoading(true);
    (async () => {
      try {
        const res = await axios.get(staffEndpoint, {
          headers,
          params: { ...staffParams, _ts: Date.now() },
        });

        const p = res?.data ?? {};
        const list = Array.isArray(p) ? p : Array.isArray(p?.data) ? p.data : [];

        if (!cancelled) setStaff(list);
      } catch {
        if (!cancelled) setStaff([]);
      } finally {
        if (!cancelled) setStaffLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, staffEndpoint, headers, staffParams]);

  // position after open
  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => calcPos());
  }, [open, calcPos]);

  // reposition on scroll/resize
  useEffect(() => {
    if (!open) return;

    const onScroll = () => calcPos();
    const onResize = () => calcPos();

    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, calcPos]);

  // close outside + ESC
  useEffect(() => {
    if (!open) return;

    const onDown = (e) => {
      const pop = popoverRef.current;
      if (!pop) return;
      if (pop.contains(e.target)) return;
      if (anchorEl && anchorEl.contains(e.target)) return;
      onClose?.();
    };

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, anchorEl, onClose]);

  const filteredStaff = useMemo(() => {
    const s = String(q || "").trim().toLowerCase();
    if (!s) return staff;

    return (Array.isArray(staff) ? staff : []).filter((u) => {
      const name = String(u?.name || "").toLowerCase();
      const username = String(u?.username || "").toLowerCase();
      const iin = String(u?.iin || "").toLowerCase();
      const phone = String(u?.phone || "").toLowerCase();
      const email = String(u?.email || "").toLowerCase();
      return (
        name.includes(s) ||
        username.includes(s) ||
        iin.includes(s) ||
        phone.includes(s) ||
        email.includes(s)
      );
    });
  }, [staff, q]);

  const toggle = useCallback(
    (id) => {
      const nid = Number(id);
      if (Number.isNaN(nid)) return;

      const next = selectedIds.includes(nid) ? selectedIds.filter((x) => x !== nid) : [...selectedIds, nid];
      onChange?.(next);
    },
    [selectedIds, onChange]
  );

  if (!open) return null;

  return createPortal(
    <div ref={popoverRef} className="asp-pop" style={{ top: pos.top, left: pos.left }} role="dialog" aria-modal="false">
      <div className="asp-head">
        <div className="asp-title">{title}</div>
        <button className="asp-x" onClick={onClose} type="button" aria-label="Close">
          ✕
        </button>
      </div>

      {subtitle ? <div className="asp-sub">{subtitle}</div> : null}

      <div className="asp-search">
        <input
          className="asp-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={searchPlaceholder}
          autoFocus
        />
      </div>

      <div className="asp-body">
        {staffLoading ? (
          <div className="asp-muted">{loadingText}</div>
        ) : (
          <div className="asp-list">
            {filteredStaff.map((u) => (
              <label key={u.id} className="asp-row">
                <input
                  className="asp-check"
                  type="checkbox"
                  checked={selectedIds.includes(u.id)}
                  onChange={() => toggle(u.id)}
                />
                <span className="asp-name">{u.name || u.full_name || u.username || `#${u.id}`}</span>
              </label>
            ))}
            {!filteredStaff.length ? <div className="asp-muted">{emptyText}</div> : null}
          </div>
        )}
      </div>

      <div className="asp-foot">
        <button className="asp-btn asp-btn--ghost" onClick={onClose} disabled={saving} type="button">
          {cancelText}
        </button>
        <button className="asp-btn asp-btn--primary" onClick={onSave} disabled={saving} type="button">
          {saving ? loadingText : saveText}
        </button>
      </div>
    </div>,
    document.body
  );
}